import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Image, Label } from 'semantic-ui-react';
import * as yup from 'yup';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FlexRow, ShadowBox } from '../elements/Common';
import { GreyLink } from '../elements/Button';
import { ErrorMessageConstant } from '../utility/constants';
import { ToastMessage } from '../utility/toast';
import { UserService } from '../utility/services/user';
import { Images } from '../images';
import { useAuth } from '../contexts/auth';
import { CommonUtility } from '../utility';
import {
  ControlledInput,
  ControlledOTPInput,
  ControlledPasswordInput,
} from '@elements/controlled-fields';
import { Button } from 'antd';

const VERIFICATION_TOKEN_LENGTH = 7;

const LoginContainer = styled.div`
  min-height: 100vh;
  position: relative;
  z-index: 2;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const PageContainer = styled.div`
  background: ${({ theme }) => theme.colors.box};
`;

const Box = styled(ShadowBox)`
  padding: 3em 2em;
  width: 432px;
  @media only screen and (max-width: 768px) {
    width: calc(100% - 20px);
  }
`;

const LoginText = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  margin-bottom: 1.5em;
`;

const VerificationMessage = styled.p`
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.lightBlack};
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const OtpFieldWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-control-input-content {
    display: flex;
    justify-content: center;
  }

  .ant-otp {
    gap: 8px;
  }

  .ant-otp-input {
    width: 44px;
    height: 48px;
    font-size: 20px;
    font-weight: 600;
    text-align: center;
    border-color: ${({ theme }) => theme.colors.semanticBorder};

    &:hover {
      border-color: ${({ theme }) => theme.colors.green};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.green};
      box-shadow: 0 0 0 2px rgba(62, 194, 71, 0.15);
    }
  }
`;

const VerificationError = styled.div`
  color: ${({ theme }) => theme.colors.red};
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 1rem;
  text-align: center;
`;

const Logo = styled(Image)`
  height: auto;
  width: 220px;
`;

const LogoContainer = styled.a`
  z-index: 9;
  margin-bottom: 50px;
`;

const LoginSchema = yup.object().shape({
  email: yup.string().required('Email is required').email('Enter a valid email'),
  password: yup.string().required('Password is required'),
});

const VerificationSchema = yup.object().shape({
  token: yup
    .string()
    .required('Verification code is required')
    .length(
      VERIFICATION_TOKEN_LENGTH,
      `Enter the ${VERIFICATION_TOKEN_LENGTH}-character code`,
    ),
});

const LoginBottomRow = styled(FlexRow)`
  justify-content: center;
  margin: 1.5rem 0 0;
`;

function VerificationDescription({ message, email }) {
  const trimmedMessage = message?.trim();

  if (trimmedMessage && email && trimmedMessage.includes(email)) {
    return <>{trimmedMessage}</>;
  }

  if (trimmedMessage) {
    return (
      <>
        {trimmedMessage} <strong>{email}</strong>
      </>
    );
  }

  return (
    <>
      A verification code has been sent to <strong>{email}</strong>
    </>
  );
}

function VerificationBox({
  loading,
  error,
  email,
  retry,
  verificationMessage,
  control,
  onSubmit,
}) {
  return (
    <Box>
      <LoginText>Further verification required</LoginText>
      <VerificationMessage>
        <VerificationDescription message={verificationMessage} email={email} />
      </VerificationMessage>
      <form onSubmit={onSubmit}>
        <OtpFieldWrap>
          <ControlledOTPInput
            name="token"
            control={control}
            length={VERIFICATION_TOKEN_LENGTH}
            size="large"
            formatter={value => value.toUpperCase()}
          />
        </OtpFieldWrap>

        {error && <VerificationError>{error}</VerificationError>}

        <Button
          loading={loading}
          type="primary"
          htmlType="submit"
          className="w-100"
          size="large"
        >
          Submit
        </Button>
      </form>
      <LoginBottomRow>
        <GreyLink
          as="button"
          type="button"
          onClick={retry}
          style={{ border: 'none', background: 'none', padding: 0 }}
        >
          Did not receive an email? Try logging in again.
        </GreyLink>
      </LoginBottomRow>
    </Box>
  );
}

VerificationBox.displayName = 'VerificationBox';

const Notice = styled.p`
  margin: -0.5em 0 1.5em;
  color: ${({ theme }) => theme.colors.lightBlack};
  font-size: 14px;
  line-height: 1.5;
`;

function LoginBox({ loading, loginClicked, error, control, notice }) {
  return (
    <Box>
      <LoginText>Welcome Back</LoginText>
      {notice && <Notice data-testid="login-notice">{notice}</Notice>}
      <form onSubmit={loginClicked}>
        <ControlledInput
          label="Email Address"
          name="email"
          control={control}
          required
          size="large"
          placeholder="Enter your email address"
        />
        <ControlledPasswordInput
          label="Password"
          name="password"
          control={control}
          size="large"
          required
          placeholder="Enter your password"
        />

        {error && (
          <Label as="h4" color="red" data-testid="error">
            {error}
          </Label>
        )}

        <Button
          loading={loading}
          type="primary"
          htmlType="submit"
          className="w-100"
          size="large"
        >
          Login
        </Button>
      </form>
      <div className="d-flex justify-content-between mt-3">
        <Link to="/forgot-password">Forget Password?</Link>
        <div className="d-flex">
          <p>New to Skupreme?</p>
          <GreyLink to="/register" className="ml-1">
            Sign Up
          </GreyLink>
        </div>
      </div>
    </Box>
  );
}
LoginBox.displayName = 'LoginBox';

export function LoginScreen() {
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [error, setError] = useState('');
  const [logging, setLogging] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const { setUserData } = useAuth();
  const navigate = useNavigate();
  // A message from the /sso landing (portal handoff that could not sign the user in).
  const { state } = useLocation();
  const notice = typeof state?.message === 'string' ? state.message : '';

  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useForm({
    resolver: yupResolver(LoginSchema),
    shouldUnregister: true,
  });

  const {
    control: verificationControl,
    handleSubmit: handleVerificationSubmit,
    reset: resetVerificationForm,
  } = useForm({
    resolver: yupResolver(VerificationSchema),
    defaultValues: { token: '' },
  });

  const redirectToApp = () => {
    if (CommonUtility.isMobileDevice()) {
      navigate('/app/fulfill/waves/list');
    } else {
      navigate('/app/fulfill/orders');
    }
  };

  const onSubmit = async data => {
    try {
      setLogging(true);
      setError('');

      const response = await UserService.login(data);
      if (response.verificationRequired) {
        setEmail(data.email);
        setVerificationMessage(response.message);
        resetVerificationForm({ token: '' });
        setVerificationRequired(true);
      } else {
        UserService.storeUser(response);
        setTimeout(() => {
          setUserData(response);
          redirectToApp();
        }, 200);
      }
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLogging(false);
    }
  };

  const onVerify = async ({ token }) => {
    try {
      setLogging(true);
      setError('');

      const response = await UserService.verify({
        token: token.trim().toUpperCase(),
      });

      UserService.storeUser(response);

      setTimeout(() => {
        setUserData(response);
        redirectToApp();
      }, 200);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLogging(false);
    }
  };

  const retry = () => {
    setError('');
    setLogging(false);
    setVerificationMessage('');
    resetVerificationForm({ token: '' });
    setVerificationRequired(false);
  };

  useEffect(() => {
    if (UserService.isAuthenticated()) {
      redirectToApp();
    }
  }, []);

  return (
    <PageContainer>
      <LoginContainer>
        <LogoContainer>
          <Logo src={Images.blackLogo} />
        </LogoContainer>
        {!!verificationRequired && (
          <VerificationBox
            email={email}
            error={error}
            loading={logging}
            retry={retry}
            verificationMessage={verificationMessage}
            control={verificationControl}
            onSubmit={handleVerificationSubmit(onVerify)}
          />
        )}
        {!verificationRequired && (
          <LoginBox
            control={control}
            errors={errors}
            loading={logging}
            loginClicked={handleSubmit(onSubmit)}
            notice={notice}
          />
        )}
      </LoginContainer>
    </PageContainer>
  );
}
